import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { Warehouse, ArrowDownToLine, ArrowUpFromLine, ArrowRightLeft, ScrollText, BarChart3, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export function InventoryModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'warehouses',
      title: 'Quản lý Kho vật lý',
      description: 'Sơ đồ kho, sức chứa, vị trí kệ hàng và danh sách kho trực thuộc.',
      icon: <Warehouse className="w-6 h-6" />,
      color: 'blue',
      path: '/inventory/warehouses',
      stats: '6 Kho hoạt động',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'receipts',
      title: 'Nhập Kho (Inbound)',
      description: 'Tiếp nhận hàng hóa, kiểm tra chất lượng (QC) và tạo phiếu nhập.',
      icon: <ArrowDownToLine className="w-6 h-6" />,
      color: 'emerald',
      path: '/inventory/receipts',
      stats: '8 Chờ nhập',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'issues',
      title: 'Xuất Kho (Outbound)',
      description: 'Xuất cấp vật tư cho Thi công, Bảo trì hoặc xuất bán thương mại.',
      icon: <ArrowUpFromLine className="w-6 h-6" />,
      color: 'amber',
      path: '/inventory/issues',
      stats: '12 Chờ xuất',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'transfers',
      title: 'Luân Chuyển & Chấm Kho',
      description: 'Điều chuyển vật tư giữa các kho, kiểm kê và bù trừ chênh lệch.',
      icon: <ArrowRightLeft className="w-6 h-6" />,
      color: 'purple',
      path: '/inventory/transfers',
      stats: '5 Đang chuyển',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'ledger',
      title: 'Sổ Cái Giao Dịch',
      description: 'Lưu trữ lịch sử mọi biến động kho không thể sửa xóa (Immutable Ledger).',
      icon: <ScrollText className="w-6 h-6" />,
      color: 'slate',
      path: '/inventory/transactions',
      stats: '12.4k Giao dịch',
      gradient: 'from-slate-500 to-gray-600',
      bgLight: 'bg-slate-100',
      textDark: 'text-slate-700'
    },
    {
      id: 'reports',
      title: 'Báo cáo Tồn Kho',
      description: 'Xem số tồn theo thời gian thực, giá vốn trung bình và vòng quay.',
      icon: <BarChart3 className="w-6 h-6" />,
      color: 'indigo',
      path: '/inventory/reports',
      stats: 'Mới cập nhật',
      gradient: 'from-indigo-500 to-blue-600',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-blue-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-blue-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
